import * as PIXI from 'pixi.js';
import { Connector, ConnectorWireRow } from '../../entities/Connector';
import { HarnessNode } from '../../entities/HarnessNode';
import { WireBundle } from '../../entities/WireBundle';
import { AppState, HarnessBackground } from '../../types';
import { logger } from '../../utils/logger';
import { sectionLengthStatus } from '../../utils/background';
import { attachBundleEvents, setupNodeEvents, setupConnectorEvents } from './entityEvents';
import { RenderContext } from './types';

/** z-index for the reference image, keeping it behind everything else. */
const BACKGROUND_Z_INDEX = -2;
/** z-index for wire bundles, keeping them behind connectors and nodes. */
const BUNDLE_Z_INDEX = -1;
/** z-index for connectors and nodes, keeping them in front of wire bundles. */
const ENTITY_Z_INDEX = 1;

/** Applies the stored world placement and opacity to the background sprite. */
function applyBackgroundTransform(sprite: PIXI.Sprite, background: HarnessBackground): void {
  sprite.x = background.x;
  sprite.y = background.y;
  sprite.scale.set(background.scale);
  sprite.alpha = background.opacity;
}

/**
 * Builds the state subscriber that reconciles application state into PIXI
 * entities: rebuilding wire bundles and adding, updating or removing connectors
 * and nodes to match the given state.
 */
export function createRenderEntities(ctx: RenderContext) {
  const { refs } = ctx;

  const renderWireBundles = (state: AppState, viewport: PIXI.Container): void => {
    const wireBundleEntities = refs.wireBundleEntities.current;

    // Bundles are rebuilt from scratch each render so they stay in the background.
    refs.bundleManager.current!.updateBundles(state.wires, state.connectors, state.nodes, state.segments);

    Array.from(wireBundleEntities.entries()).forEach(([id, wireBundle]) => {
      viewport.removeChild(wireBundle);
      wireBundleEntities.delete(id);
    });

    refs.bundleManager.current!.getBundles().forEach(bundleData => {
      // With a background in place, mark each section's length as matching the
      // layout ('auto') or overwritten so the badge border can reflect it.
      if (state.background) {
        bundleData.segments.forEach(seg => {
          const calculated = ctx.stateManager.suggestedSegmentLength(seg.aId, seg.bId);
          seg.lengthStatus = sectionLengthStatus(seg.length, calculated);
        });
      }
      const wireBundle = new WireBundle(bundleData);
      wireBundle.zIndex = BUNDLE_Z_INDEX;
      attachBundleEvents(wireBundle, bundleData, state, viewport, ctx);
      wireBundleEntities.set(bundleData.id, wireBundle);
      viewport.addChild(wireBundle);
    });
  };

  const renderBackground = (state: AppState, viewport: PIXI.Container): void => {
    const background = state.background;

    if (!background) {
      const sprite = refs.backgroundSprite.current;
      if (sprite) {
        viewport.removeChild(sprite);
        sprite.destroy();
        refs.backgroundSprite.current = null;
      }
      refs.backgroundSrc.current = null;
      return;
    }

    // Only (re)load the texture when the underlying image data actually changes;
    // moving, scaling or fading it just updates the existing sprite's transform.
    if (refs.backgroundSrc.current !== background.src) {
      refs.backgroundSrc.current = background.src;
      const srcAtRequest = background.src;
      PIXI.Assets.load(background.src)
        .then((texture: PIXI.Texture) => {
          // Bail if the component unmounted or the image changed again meanwhile.
          if (!refs.mounted.current || refs.backgroundSrc.current !== srcAtRequest) {
            return;
          }
          const previous = refs.backgroundSprite.current;
          if (previous) {
            viewport.removeChild(previous);
            previous.destroy();
          }
          const sprite = new PIXI.Sprite(texture);
          sprite.zIndex = BACKGROUND_Z_INDEX;
          // The React overlay handles dragging, so the sprite never intercepts events.
          sprite.eventMode = 'none';
          const current = ctx.stateManager.getState().background;
          if (current) {
            applyBackgroundTransform(sprite, current);
          }
          refs.backgroundSprite.current = sprite;
          viewport.addChild(sprite);
        })
        .catch(error => logger.pixi.warn('Failed to load background image', error));
      return;
    }

    const sprite = refs.backgroundSprite.current;
    if (sprite && !sprite.destroyed) {
      applyBackgroundTransform(sprite, background);
    }
  };

  const renderConnectors = (state: AppState, viewport: PIXI.Container): void => {
    const connectorEntities = refs.connectorEntities.current;
    const currentConnectorIds = new Set(state.connectors.map(t => t.id));

    Array.from(connectorEntities.entries()).forEach(([id, connector]) => {
      if (!currentConnectorIds.has(id)) {
        viewport.removeChild(connector);
        connectorEntities.delete(id);
      }
    });

    state.connectors.forEach(connectorData => {
      let connector = connectorEntities.get(connectorData.id);
      if (!connector) {
        connector = new Connector(connectorData);
        connector.zIndex = ENTITY_Z_INDEX;
        setupConnectorEvents(connector, connectorData, ctx);
        connectorEntities.set(connectorData.id, connector);
        viewport.addChild(connector);
      } else {
        connector.x = connectorData.xPosition;
        connector.y = connectorData.yPosition;
        connector.setName(connectorData.name);
      }

      const pins = connectorData.pins ?? [];
      const connectedWires = state.wires.filter(w =>
        w.startConnectorId === connectorData.id || w.endConnectorId === connectorData.id
      );

      // Group wires under the pin they terminate on at this connector; wires with
      // no pin here fall into the unassigned bucket.
      const wiresByPin = new Map<string, ConnectorWireRow[]>();
      const unassigned: ConnectorWireRow[] = [];
      const pushToPin = (pinId: string, row: ConnectorWireRow) => {
        const rows = wiresByPin.get(pinId) ?? [];
        rows.push(row);
        wiresByPin.set(pinId, rows);
      };

      connectedWires.forEach(w => {
        const row: ConnectorWireRow = { id: w.id, name: w.name, gauge: w.gauge, color: w.color, stripeColor: w.stripeColor };
        let placed = false;
        if (w.startConnectorId === connectorData.id && w.startPinId) {
          pushToPin(w.startPinId, row);
          placed = true;
        }
        if (w.endConnectorId === connectorData.id && w.endPinId) {
          pushToPin(w.endPinId, row);
          placed = true;
        }
        if (!placed) {
          unassigned.push(row);
        }
      });

      connector.updateContent({ pins, wiresByPin, unassigned });
    });
  };

  const renderNodes = (state: AppState, viewport: PIXI.Container): void => {
    const nodeEntities = refs.nodeEntities.current;
    const currentNodeIds = new Set(state.nodes.map(n => n.id));

    Array.from(nodeEntities.entries()).forEach(([id, node]) => {
      if (!currentNodeIds.has(id)) {
        viewport.removeChild(node);
        nodeEntities.delete(id);
      }
    });

    state.nodes.forEach(nodeData => {
      let node = nodeEntities.get(nodeData.id);
      if (!node) {
        node = new HarnessNode(nodeData);
        node.zIndex = ENTITY_Z_INDEX;
        setupNodeEvents(node, nodeData, ctx);
        nodeEntities.set(nodeData.id, node);
        viewport.addChild(node);
      } else {
        node.x = nodeData.xPosition;
        node.y = nodeData.yPosition;
      }
    });
  };

  return (state: AppState): void => {
    if (!refs.mounted.current || !refs.viewportContainer.current || !refs.bundleManager.current) {
      return;
    }

    const viewport = refs.viewportContainer.current;
    viewport.sortableChildren = true;

    // Reference image at the very back, then wire bundles, then connectors and nodes.
    renderBackground(state, viewport);
    renderWireBundles(state, viewport);
    renderConnectors(state, viewport);
    renderNodes(state, viewport);
  };
}
